/**
 * Putting a dye into an item.
 *
 * The catalogue in [`wardrobe.generated.ts`](./wardrobe.generated.ts) is shapes
 * with holes in them: `{T}` for the dye, `{TL}` and `{TD}` for the highlight and
 * shadow **derived from it**. Deriving the other two is what makes a dye one
 * input rather than a repaint, and it is why an item nobody has dyed still has
 * volume ([D-041](../../../docs/DECISIONS.md#d-041)).
 *
 * There are no image files. An item is markup the app renders inline, so the
 * twelve dyes cost twelve strings rather than twelve drawings — and a thirteenth
 * would cost one line rather than thirty-eight more pictures.
 */
import { DYES, WARDROBE, } from './wardrobe.generated.js';
export * from './wardrobe.generated.js';
const clamp = (n) => (n < 0 ? 0 : n > 255 ? 255 : Math.round(n));
const channels = (hex) => {
    const full = hex.length === 4
        ? `#${hex[1] ?? ''}${hex[1] ?? ''}${hex[2] ?? ''}${hex[2] ?? ''}${hex[3] ?? ''}${hex[3] ?? ''}`
        : hex;
    return [
        Number.parseInt(full.slice(1, 3), 16),
        Number.parseInt(full.slice(3, 5), 16),
        Number.parseInt(full.slice(5, 7), 16),
    ];
};
const hex = (n) => clamp(n).toString(16).padStart(2, '0');
/**
 * Lighten toward white, or darken toward a **warm** black.
 *
 * The same curve the mockup builder uses, deliberately: a shadow that goes to
 * pure grey reads as dirt on a colour this warm. Darkening toward (26, 22, 18)
 * keeps a dyed item looking like an object with a light on it.
 */
export function shade(colour, amount) {
    const [r, g, b] = channels(colour);
    if (amount >= 0) {
        return `#${hex(r + (255 - r) * amount)}${hex(g + (255 - g) * amount)}${hex(b + (255 - b) * amount)}`;
    }
    const a = -amount;
    return `#${hex(r * (1 - a) + 26 * a)}${hex(g * (1 - a) + 22 * a)}${hex(b * (1 - a) + 18 * a)}`;
}
/** How far the highlight and the shadow sit from the dye. */
export const LIT = 0.14;
export const EDGE = -0.16;
/**
 * One item, in one colour, as SVG ready to drop into the avatar.
 *
 * `dye` is optional: without one the item is drawn in the colour it arrives in,
 * which is what a chest hands over and what the shop shows before anybody has
 * chosen anything.
 */
export function dyed(item, dye) {
    const base = dye === undefined ? item.tint : DYES[dye];
    return item.body
        .replaceAll('{TL}', shade(base, LIT))
        .replaceAll('{TD}', shade(base, EDGE))
        .replaceAll('{T}', base);
}
const BY_ID = new Map(WARDROBE.map((item) => [item.id, item]));
/** An item by id, or undefined — a saved outfit can name one that has gone. */
export const wardrobeItem = (id) => BY_ID.get(id);
/** Everything in one slot, in catalogue order, which is the order a shop lists. */
export const itemsInSlot = (slot) => WARDROBE.filter((item) => item.slot === slot);
//# sourceMappingURL=wardrobe.js.map